import React from "react";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import UList from "@insightfulscience/atomic-react/UList";
import { Row, Col } from "@insightfulscience/atomic-react/Grid";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import PageNavbar from "@insightfulscience/atomic-react/PageNavbar";
import PageNavbarItem from "@insightfulscience/atomic-react/PageNavbar/Item";
import PageNavbarLink from "@insightfulscience/atomic-react/PageNavbar/Link";
import LandingSimpleSectionStripped from "@insightfulscience/atomic-react/LandingElement/SimpleSectionStripped";
import Heading from "@insightfulscience/atomic-react/Heading";
import Feature from "@insightfulscience/atomic-react/Feature";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import Button from "@insightfulscience/atomic-react/Button";
import Line from "@insightfulscience/atomic-react/Line";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import NextLink from "@insightfulscience/smart-react/NextLink";
import { createAndRaise } from "@insightfulscience/shared-utils/fn/create-and-raise";
import ApplicationError from "@insightfulscience/smart-react/ApplicationError";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "@src/SiteLayout";
import TryForFree from "@src/TryForFree";
import routes from "@src/routes";
import { isRouteEnable } from "@src/routing-utils";
import WithServices from "@src/services";
import withErrorHandling from "@src/AppError/withErrorHandling";
import resources from "../../locales";

const img1 = "/images/dotmatics/omiq/features/img-1.png";
const img2 = "/images/dotmatics/omiq/features/img-2.png";
const img3 = "/images/dotmatics/omiq/features/img-3.png";
const img4 = "/images/dotmatics/omiq/features/img-4.png";
const img5 = "/images/dotmatics/omiq/features/img-5.png";
const img6 = "/images/dotmatics/omiq/features/img-6.png";
const img7 = "/images/dotmatics/omiq/features/img-7.png";
const img8 = "/images/dotmatics/omiq/features/img-8.png";
const img9 = "/images/dotmatics/omiq/features/img-9.png";
const img10 = "/images/dotmatics/omiq/features/img-10.png";
const img11 = "/images/dotmatics/omiq/features/img-11.png";
const img12 = "/images/dotmatics/omiq/features/img-12.png";
const img13 = "/images/dotmatics/omiq/features/img-13.png";
const img14 = "/images/dotmatics/omiq/features/img-14.png";
const img15 = "/images/dotmatics/omiq/features/img-15.png";
const img16 = "/images/dotmatics/omiq/features/img-16.png";
const img17 = "/images/dotmatics/omiq/features/img-17.png";

const FeaturesPage = () => {
	const [t] = useLocale("feature-page");

	return (
		<SiteLayout>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")} />
				<PageMeta.Canonical href={t("META_CANONICAL")} />
			</PageMeta>
			<Hero color="6" className="py-18">
				<ContentContainer lg={11} center>
					<HeroTitle>Single Cell Analysis, Made Simple</HeroTitle>
					<HeroDescription>
						Complete your entire workflow in one software, from raw data to statistical
						significance, using familiar tools seamlessly blended with cutting-edge computational
						methods and visualizations.
					</HeroDescription>
					<HeroRow className="mt-6">
						<HeroCol md={3}>
							<NextLink
								Component={HeroButtonLink}
								href={routes.freeTrial.url}
								isBlock
								styleType="secondary"
								className="section-hero__button"
							>
								Try for Free
							</NextLink>
						</HeroCol>
						<HeroCol md={3}>
							<NextLink
								Component={HeroButtonLink}
								href={routes.pricing.url}
								styleType="secondary"
								isOutline2
								className="section-hero__button"
								isBlock
							>
								Pricing
							</NextLink>
						</HeroCol>
					</HeroRow>
				</ContentContainer>
			</Hero>
			<PageNavbar alignHorizontal="around" bgColor="transparent" withBorder>
				<PageNavbarItem>
					<PageNavbarLink href="#high-dimensional-analysis">
						High Dimensional Analysis
					</PageNavbarLink>
				</PageNavbarItem>
				<PageNavbarItem>
					<PageNavbarLink href="#classic-analysis">Classical Analysis</PageNavbarLink>
				</PageNavbarItem>
				<PageNavbarItem>
					<PageNavbarLink href="#cloud-platform">Cloud Platform</PageNavbarLink>
				</PageNavbarItem>
				<PageNavbarItem>
					<PageNavbarLink href="#autogating">Autogating</PageNavbarLink>
				</PageNavbarItem>
			</PageNavbar>
			<LandingSimpleSectionStripped id="high-dimensional-analysis">
				<LandingSimpleSection.Title>High-Dimensional Power</LandingSimpleSection.Title>
				<LandingSimpleSection.DescriptionV2>
					Use OMIQ&apos;s suite of machine learning tools to bring order to complex high-dimensional
					data.
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Feature px={false}>
						<FeatureMeta md={5} indent={false}>
							<FeatureMeta.Title>Dimensionality Reduction</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>
									Run t-SNE, UMAP, PCA, EmbedSOM, FIt-SNE, TriMap, and more. Use opt-SNE to remove
									the guesswork with settings and get great results every time.
								</Paragraph>
								<Paragraph>
									Take advantage of GPU-accelerated processing options to get results over 100x
									faster in some cases.
								</Paragraph>
							</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage src={img1} md={7} isImageFrame />
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img2} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Clustering</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Categorize data into populations automatically with FlowSOM, Phenograph, PARC,
								SPADE, ClusterX, and more.
							</Paragraph>
							<Paragraph>
								Use graph analysis or heatmaps to deepen your understanding of stucture and patterns
								within data.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Statistical Differential Analysis</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Cut through the noise and find what matters. OMIQ has visualizations and statistical
								methods to find the strongest stratifying signals aligned with clinical or
								experimental metadata.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img3} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img4} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Trajectory Inference</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Discover and visualize paths of cellular development. Understand differentation
								switch points and cell fate.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Automated Data Cleaning</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Everything starts with clean data. Incorporate algorithms such as flowAI and flowCut
								into your workflow to help automate and standardize this key step of analysis.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img5} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img6} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Normalization</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Tame batch effects and other unwanted signal differences between files in order to
								improve comparability for analysis and visualization.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
				<Row className="mt-26">
					<Col center>
						<Button.Link href={routes.freeTrial.url} styleType="secondary" isOutline>
							Try OMIQ Free
						</Button.Link>
					</Col>
				</Row>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped id="classic-analysis">
				<Row className="mb-16">
					<Col>
						<Heading Tag="h2" size="xl" center>
							Classical Analysis
						</Heading>
						<Paragraph size="l" center className="mb-12">
							OMIQ has you covered for your classic manual analysis needs
						</Paragraph>
					</Col>
				</Row>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Compensation and Scaling</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Convenient functionality for adjusting compensation and setting scales.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img7} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img8} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Gating</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Interactive display boundaries for plots. Filter data down by gate or filter using
								subsampling.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Figures and Reporting</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Integrated figures and reporting allow you to achieve meaningful results faster.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img9} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img10} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Export Results</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Export individual plots or batch reports from an OMIQ Figure. Multiple formats are
								available, such as PNG, SVG, PDF, and PPT.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
				<Row className="mt-26">
					<Col center>
						<Button.Link href={routes.freeTrial.url} styleType="secondary" isOutline>
							Try OMIQ Free
						</Button.Link>
					</Col>
				</Row>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped id="cloud-platform">
				<Row className="mb-16">
					<Col>
						<Heading Tag="h2" size="xl" center>
							Modern Cloud Platform
						</Heading>
						<Paragraph size="l" center className="mb-12">
							Powerful, easy-to-use software that accelerates your journey from data to insights
						</Paragraph>
					</Col>
				</Row>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Share and Collaborate</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Choose permission levels and add colleagues with a click. Get to results faster,
								together.
							</Paragraph>
							<Paragraph>
								Form connections, share datasets, share workflows, adjust roles and permissions and
								create easy to use groups for collaboration.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img11} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img12} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Secure and Auditable</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Your data is safe with OMIQ&apos;s enterprise-grade security, including end-to-end
								and at-rest encryption, and organizational security-in-depth.
							</Paragraph>
							<Paragraph>
								All analysis in OMIQ is stored as a traceable sequence of steps called a Workflow.
								Workflows can be locked for safekeeping.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Flexible Data Input</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								FCS, CSV, LMD. Regardless of your input format, OMIQ will accept it. Clinical and
								experimental metadata can be cross-referenced manually or automatically to help OMIQ
								extract the insights that matter.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img13} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img14} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Customizable Plugins and Scripts</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								With plugins, your code can run inside OMIQ. Share your algorithms with the world or
								keep them private for your enterprise only.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
				<Row className="mt-26">
					<Col center>
						<Button.Link href={routes.freeTrial.url} styleType="secondary" isOutline>
							Try OMIQ Free
						</Button.Link>
					</Col>
				</Row>
			</LandingSimpleSection>
			<LandingSimpleSectionStripped id="autogating">
				<Row className="mb-16">
					<Col>
						<Heading Tag="h2" size="xl" center>
							Autogating Services
							<span className="ml-3 font-size--14 text-weight--bold text-color-2">ADD-ON</span>
						</Heading>
						<Paragraph size="l" center className="mb-12">
							Bespoke automated data analysis pipelines for cytometry data providing rapid, robust
							and reproducible results
						</Paragraph>
					</Col>
				</Row>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Automated Gating</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Automated gating pipelines are collaboratively customized for each client to
								robustly reproduce your existing gating hierarchy.
							</Paragraph>
							<Paragraph>
								Once the pipeline is designed, our gates will adjust themselves for each sample
								using a robust, reproducible framework.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img15} md={7} isImageFrame />
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone bgColor="4">
				<Feature px={false}>
					<FeatureImage src={img16} md={7} isImageFrame />
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>Quality Checking</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								With our automated approaches, the data QCs itself. We build the checks into the
								gating process to make sure FCS files are internally consistent, match with patient
								files, and that all the reportables that need to be filled out are done correctly.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSection>
			<LandingSimpleSection>
				<Feature px={false}>
					<FeatureMeta md={5} indent={false}>
						<FeatureMeta.Title>OMIQ Integration</FeatureMeta.Title>
						<FeatureMeta.Description>
							<Paragraph>
								Reusable R pipeline integrates seamlessly into OMIQ. Produced gates are native OMIQ
								format. Built-in compliance with OMIQ with no IT involvement.
							</Paragraph>
						</FeatureMeta.Description>
					</FeatureMeta>
					<FeatureImage src={img17} md={7} isImageFrame />
				</Feature>
				<Row className="mt-26">
					<Col center>
						<Button.Link href={routes.external.help} styleType="secondary" isOutline>
							Contact Us
						</Button.Link>
					</Col>
				</Row>
			</LandingSimpleSection>
			<Line />
			<LandingSimpleSection isBorderNone className="mb-20">
				<Row className="mb-16">
					<Col>
						<Heading Tag="h2" size="xl" center>
							OMIQ Features
						</Heading>
						<Paragraph size="l" center className="mb-12">
							Explore a list of OMIQ’s features and services
						</Paragraph>
					</Col>
				</Row>
				<Row mx my>
					<Col md={6} className="bordered-black-20 px-10 py-8">
						<Heading Tag="h3" className="mt-0">
							Classic Analysis
						</Heading>
						<UList>
							<UList.Item>Adjust compensation</UList.Item>
							<UList.Item>Set scales</UList.Item>
							<UList.Item>Gating</UList.Item>
							<UList.Item>Make figures</UList.Item>
							<UList.Item>Visual batch reports</UList.Item>
							<UList.Item>Statistical batch reports</UList.Item>
						</UList>
					</Col>
					<Col md={6} className="bordered-black-20 px-10 py-8">
						<Heading Tag="h3" className="mt-0">
							Data Management
						</Heading>
						<UList>
							<UList.Item>Reproducible workflows</UList.Item>
							<UList.Item>Import fcs, csv and lmd files</UList.Item>
							<UList.Item>Customizable plugins</UList.Item>
							<UList.Item>Auditable and reproducible</UList.Item>
							<UList.Item>Enterprise grade security</UList.Item>
							<UList.Item>GxP compliance</UList.Item>
							<UList.Item>Sharing and collaboration</UList.Item>
						</UList>
					</Col>
					<Col md={6} className="bordered-black-20 px-10 py-8">
						<Heading Tag="h3" className="mt-0">
							High Dimensional Analysis
						</Heading>
						<UList>
							<UList.Item>
								Dimensionality Reduction with t-SNE, UMAP, PCA, EmbedSOM, FIt-SNE, TriMap
							</UList.Item>
							<UList.Item>Clustering with FlowSOM, Phenograph, PARC, SPADE, Cluster</UList.Item>
							<UList.Item>Statistical differential analysis</UList.Item>
							<UList.Item>Trajectory interference</UList.Item>
							<UList.Item>Automated data cleaning with flowAI and flowCut</UList.Item>
							<UList.Item>Normalization</UList.Item>
						</UList>
					</Col>
					<Col md={6} className="bordered-black-20 px-10 py-8">
						<Heading Tag="h3" className="mt-0">
							Autogating
						</Heading>
						<UList>
							<UList.Item>Automated gating</UList.Item>
							<UList.Item>Biomarker discovery</UList.Item>
							<UList.Item>Quality checking</UList.Item>
							<UList.Item>Customized reports</UList.Item>
							<UList.Item>Reusable R pipeline</UList.Item>
						</UList>
					</Col>
				</Row>
			</LandingSimpleSection>
			<TryForFree />
		</SiteLayout>
	);
};

FeaturesPage.preConditions = [
	[() => isRouteEnable(routes.features), createAndRaise(ApplicationError.NotFound)],
];

export default pipe(
	FeaturesPage,
	WithLocale({ resources }),
	WithConditions,
	withErrorHandling,
	WithServices,
);
